if (!customElements.get('back-in-stock')) {
  customElements.define(
    'back-in-stock',
    class BackInStock extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('.back-in-stock__form');
        this.emailInput = this.querySelector('input[type="email"]');
        this.submitButton = this.querySelector('button[type="button"]');
        this.submitText = this.querySelector('.back-in-stock__submit-text');
        this.spinner = this.querySelector('.back-in-stock__spinner');
        this.successMessage = this.querySelector('.back-in-stock__message--success');
        this.errorMessage = this.querySelector('.back-in-stock__message--error');
        this.errorText = this.querySelector('.back-in-stock__error-text');

        this.klaviyoApiKey = this.dataset.klaviyoApiKey;
        this.productId = this.dataset.productId;
        this.variantId = this.dataset.variantId;

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        }
      }

      connectedCallback() {
        if (typeof $ !== 'undefined') {
          this.bindVariantChangeListener();
        }
      }

      disconnectedCallback() {
        if (this.$productForm) {
          this.$productForm.off('variantChanged', this.variantChangeHandler);
        }
      }

      bindVariantChangeListener() {
        const productFormContainer =
          this.closest('.product-form') ||
          document.querySelector(`.product-form[data-product-id="${this.productId}"]`);
        if (!productFormContainer) return;
        this.$productForm = $(productFormContainer);

        if (this.$productForm.length) {
          this.variantChangeHandler = (event, variant, productData) => {
            this.handleVariantChange(variant);
          };

          this.$productForm.on('variantChanged', this.variantChangeHandler);
        }
      }

      handleVariantChange(variant) {
        if (variant) {
          this.variantId = variant.id;
          this.dataset.variantId = variant.id;

          if (variant.title) {
            this.dataset.variantTitle = variant.title;
          }

          if (!variant.available || variant.available === false) {
            this.show();
          } else {
            this.hide();
          }

          this.resetForm();
        }
      }

      show() {
        this.classList.remove('hidden');
      }

      hide() {
        this.classList.add('hidden');
      }

      resetForm() {
        if (this.form) {
          if (this.emailInput) this.emailInput.value = '';
          this.form.classList.remove('hidden');
        }
        if (this.successMessage) {
          this.successMessage.classList.add('hidden');
        }
        if (this.errorMessage) {
          this.errorMessage.classList.add('hidden');
        }
        if (this.submitButton) {
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('loading');
        }
      }

      async onSubmit() {
        const email = this.emailInput.value.trim();

        if (!email || !this.isValidEmail(email)) {
          this.showError(
            window.backInStockStrings?.invalidEmail || 'Please enter a valid email address'
          );
          return;
        }

        if (!this.klaviyoApiKey) {
          this.showError(
            window.backInStockStrings?.configError || 'Configuration error. Please contact support.'
          );
          console.error('Back-in-Stock: Klaviyo API key not configured');
          return;
        }

        this.setLoading(true);

        try {
          const response = await this.subscribeToBackInStock(email);

          if (response.success) {
            this.showSuccess();
          } else if (response.isDuplicate) {
            this.showError(
              window.backInStockStrings?.alreadySubscribed ||
                'This email is already subscribed for notifications on this product.'
            );
          } else {
            this.showError(
              response.error ||
                window.backInStockStrings?.genericError ||
                'Something went wrong. Please try again.'
            );
          }
        } catch (error) {
          console.error('Back-in-Stock subscription error:', error);
          this.showError(
            window.backInStockStrings?.genericError || 'Something went wrong. Please try again.'
          );
        } finally {
          this.setLoading(false);
        }
      }

      async subscribeToBackInStock(email) {
        const endpoint = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.klaviyoApiKey}`;

        const payload = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                  },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.variantId}`,
                },
              },
            },
          },
        };

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-06-15',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          return { success: true };
        }

        let errorMessage = null;
        let isDuplicate = false;

        try {
          const errorData = await response.json();
          errorMessage = errorData?.errors?.[0]?.detail || null;
          const errorCode = errorData?.errors?.[0]?.code || null;

          if (errorMessage || errorCode) {
            const combinedError = `${errorMessage} ${errorCode}`.toLowerCase();
            if (combinedError.includes('already subscribed') ||
                combinedError.includes('duplicate') ||
                combinedError.includes('already exists')) {
              isDuplicate = true;
            }
          }
        } catch {
        }

        return {
          success: false,
          error: errorMessage,
          isDuplicate: isDuplicate,
        };
      }

      isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      }

      setLoading(isLoading) {
        if (this.submitButton) {
          this.submitButton.disabled = isLoading;
          this.submitButton.classList.toggle('loading', isLoading);
        }
        if (this.spinner) {
          this.spinner.classList.toggle('hidden', !isLoading);
        }
      }

      showSuccess() {
        if (this.form) {
          this.form.classList.add('hidden');
        }
        if (this.errorMessage) {
          this.errorMessage.classList.add('hidden');
        }
        if (this.successMessage) {
          this.successMessage.classList.remove('hidden');
        }
      }

      showError(message) {
        if (this.errorText) {
          this.errorText.textContent = message;
        }
        if (this.errorMessage) {
          this.errorMessage.classList.remove('hidden');
        }
        if (this.successMessage) {
          this.successMessage.classList.add('hidden');
        }
      }
    }
  );
}
